import React, {Dispatch, SetStateAction, useMemo, useState} from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import {useDebouncedValue, useDisclosure} from "@mantine/hooks";
import {IconArrowDown, IconArrowUp, IconPlus, IconSearch, IconTrash} from "@tabler/icons-react";
import {CustomFieldProfileCatalogItem} from "@ots-frontend/ots-data";
import {CustomFieldProfileFormProps} from "./CustomFieldProfileForm";

export type CustomFieldProfileFieldPickerProps = Pick<CustomFieldProfileFormProps, 'options'> & {
  catalog: CustomFieldProfileCatalogItem[]
  setCatalog: Dispatch<SetStateAction<CustomFieldProfileCatalogItem[]>>
}

export const CustomFieldProfileFieldPicker = (props: CustomFieldProfileFieldPickerProps) => {
  const {options, catalog, setCatalog} = props

  const [search, setSearch] = useState("");
  const [q] = useDebouncedValue(search, 150);

  const selected = useMemo(() => {
    return [...catalog]
      .filter((x) => x.selected)
      .sort((a, b) => (a.profileItemSortOrder ?? 0) - (b.profileItemSortOrder ?? 0));
  }, [catalog]);

  const eligible = useMemo(() => {
    const base = catalog.filter((x) => !x.selected);
    const qq = q.trim().toLowerCase();
    if (!qq) return base;
    return base.filter((x) => {
      return (
        x.code.toLowerCase().includes(qq) ||
        x.defObjectType.toLowerCase().includes(qq) ||
        x.dataType.toLowerCase().includes(qq) ||
        (x.refObjectType ?? "").toLowerCase().includes(qq)
      );
    });
  }, [catalog, q]);

  const [addModalOpen, addModal] = useDisclosure(false);
  const [pendingDefId, setPendingDefId] = useState<string | null>(null);
  const [pendingLabel, setPendingLabel] = useState("");
  const [pendingRequired, setPendingRequired] = useState(false);

  const pending = useMemo(
    () => (pendingDefId ? catalog.find((x) => x.defId === pendingDefId) ?? null : null),
    [catalog, pendingDefId]
  );

  function openAdd(defId: string) {
    const def = catalog.find((x) => x.defId === defId);
    if (!def) return;
    setPendingDefId(defId);
    setPendingLabel(humanizeCode(def.code));
    setPendingRequired(false);
    addModal.open();
  }

  function confirmAdd() {
    if (!pendingDefId) return;
    const label = pendingLabel.trim();
    if (!label) return;

    const nextSort = selected.length === 0 ? 0 : Math.max(...selected.map((s) => s.profileItemSortOrder ?? 0)) + 10;

    setCatalog((prev) =>
      prev.map((x) =>
        x.defId === pendingDefId
          ? {
            ...x,
            selected: true,
            profileItemId: x.profileItemId ?? `temp:${x.defId}`,
            profileItemLabel: label,
            profileItemRequired: pendingRequired,
            profileItemSortOrder: nextSort,
          }
          : x
      )
    );

    addModal.close();
    setPendingDefId(null);
  }

  function remove(defId: string) {
    setCatalog((prev) =>
      prev.map((x) =>
        x.defId === defId
          ? {
            ...x,
            selected: false,
            profileItemId: null,
            profileItemLabel: null,
            profileItemRequired: null,
            profileItemSortOrder: null
          }
          : x
      )
    );
  }

  function updateSelected(defId: string, patch: Partial<Pick<CustomFieldProfileCatalogItem, "profileItemLabel" | "profileItemRequired">>) {
    setCatalog((prev) => prev.map((x) => (x.defId === defId ? {...x, ...patch} : x)));
  }

  function move(defId: string, dir: "up" | "down") {
    const list = selected;
    const idx = list.findIndex((x) => x.defId === defId);
    if (idx < 0) return;
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= list.length) return;

    const reordered = [...list];
    [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];

    const newSortById = new Map<string, number>();
    reordered.forEach((x, i) => newSortById.set(x.defId, i * 10));

    setCatalog((prev) =>
      prev.map((x) => (x.selected ? {
        ...x,
        profileItemSortOrder: newSortById.get(x.defId) ?? x.profileItemSortOrder
      } : x))
    );
  }

  return (
    <Stack spacing="sm">
      <Group position="apart">
        <Text fw={600}>Fields</Text>
        <Badge variant="light">{selected.length}</Badge>
      </Group>

      <TextInput
        size="xs"
        icon={<IconSearch size={16}/>}
        placeholder="Search eligible fields…"
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />

      <Group grow align="flex-start" spacing="sm">
        <Card withBorder radius="md" p="sm">
          <Stack spacing="xs">
            <Group position="apart">
              <Text size="sm" fw={600}>Eligible</Text>
              <Badge variant="light">{eligible.length}</Badge>
            </Group>

            <ScrollArea h={360} offsetScrollbars>
              <Stack spacing="xs">
                {eligible.length === 0 && (
                  <Text size="xs" c="dimmed">No eligible fields (or all selected).</Text>
                )}

                {eligible.map((def) => (
                  <Card key={def.defId} withBorder radius="md" p="xs">
                    <Group position="apart" align="flex-start" noWrap>
                      <Box style={{flex: 1}}>
                        <Group spacing={6}>
                          <Text fw={600} size="xs">{def.code}</Text>
                          <Badge variant="light" size="xs">{def.dataType}</Badge>
                        </Group>
                        <Text size="xs" c="dimmed">{def.defObjectType}</Text>
                      </Box>

                      <ActionIcon size="sm" variant="light" onClick={() => openAdd(def.defId)} title="Add">
                        <IconPlus size={14}/>
                      </ActionIcon>
                    </Group>
                  </Card>
                ))}
              </Stack>
            </ScrollArea>
          </Stack>
        </Card>

        <Card withBorder radius="md" p="sm">
          <Stack spacing="xs">
            <Group position="apart">
              <Text size="sm" fw={600}>In profile</Text>
              <Badge variant="light">{selected.length}</Badge>
            </Group>

            <ScrollArea h={360} offsetScrollbars>
              <Stack spacing="xs">
                {selected.length === 0 && (
                  <Text size="xs" c="dimmed">Add fields from the left.</Text>
                )}

                {selected.map((item, idx) => (
                  <Card key={item.defId} withBorder radius="md" p="xs">
                    <Stack spacing={6}>
                      <Group position="apart" align="flex-start" noWrap>
                        <Box style={{flex: 1}}>
                          <Group spacing={6}>
                            <Text fw={700} size="xs">{item.code}</Text>
                            <Badge variant="light" size="xs">{item.dataType}</Badge>
                          </Group>
                          <Text size="xs" c="dimmed">{item.defObjectType}</Text>
                        </Box>

                        <Group spacing={4}>
                          <ActionIcon size="sm" variant="subtle" disabled={idx === 0} onClick={() => move(item.defId, "up")}>
                            <IconArrowUp size={14}/>
                          </ActionIcon>
                          <ActionIcon size="sm" variant="subtle" disabled={idx === selected.length - 1} onClick={() => move(item.defId, "down")}>
                            <IconArrowDown size={14}/>
                          </ActionIcon>
                          <ActionIcon size="sm" color="red" variant="light" disabled={typeof options?.customFieldProfileId === "undefined" || typeof item.profileItemId === "undefined"} onClick={() => remove(item.defId)}>
                            <IconTrash size={14}/>
                          </ActionIcon>
                        </Group>
                      </Group>

                      <TextInput
                        size="xs"
                        label="Label"
                        value={item.profileItemLabel ?? ""}
                        onChange={(e) => updateSelected(item.defId, {profileItemLabel: e.currentTarget.value})}
                        maxLength={50}
                      />

                      <Checkbox
                        size="xs"
                        label="Required"
                        checked={!!item.profileItemRequired}
                        onChange={(e) => updateSelected(item.defId, {profileItemRequired: e.currentTarget.checked})}
                      />
                    </Stack>
                  </Card>
                ))}
              </Stack>
            </ScrollArea>
          </Stack>
        </Card>
      </Group>

      <Modal opened={addModalOpen} onClose={addModal.close} title="Add field" centered>
        <Stack spacing="sm">
          {pending && (
            <Card withBorder radius="md" p="sm">
              <Group spacing={6}>
                <Text fw={700} size="sm">{pending.code}</Text>
                <Badge variant="light">{pending.dataType}</Badge>
                <Badge variant="outline">{pending.defObjectType}</Badge>
              </Group>
            </Card>
          )}

          <TextInput
            label="Label"
            withAsterisk
            value={pendingLabel}
            onChange={(e) => setPendingLabel(e.currentTarget.value)}
            maxLength={50}
          />

          <Checkbox
            label="Required"
            checked={pendingRequired}
            onChange={(e) => setPendingRequired(e.currentTarget.checked)}
          />

          <Group position="right">
            <Button variant="default" onClick={addModal.close}>Cancel</Button>
            <Button onClick={confirmAdd}>Add</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

function humanizeCode(code: string): string {
  return (code ?? "")
    .split("_")
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(" ");
}
